import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import { formatCoverageModeLabel } from '../../utils/coverageDashboardUtils';

interface AgentTimelineModalProps {
  open: boolean;
  onClose: () => void;
  employeeId: string;
  employeeName: string;
  date: string;
}

const AgentTimelineModal: React.FC<AgentTimelineModalProps> = ({
  open,
  onClose,
  employeeId,
  employeeName,
  date,
}) => {
  const { data, isLoading } = useQuery({
    queryKey: ['agent-timeline', employeeId, date],
    queryFn: async () => {
      const response = await apiClient.operations.getAgentTimeline(employeeId, { date });
      return response.data.data;
    },
    enabled: open && Boolean(employeeId),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Agent Timeline — {employeeName}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              Clock In: {data?.clockIn || '-'} | Clock Out: {data?.clockOut || '-'}
            </Typography>
            {data?.originalSchedule && (
              <Typography variant="body2" color="text.secondary">
                Original Schedule: {data.originalSchedule.campaignName} ({data.originalSchedule.startTime} - {data.originalSchedule.endTime})
              </Typography>
            )}

            <Typography variant="subtitle2">Campaign Assignment Timeline</Typography>
            {(data?.assignments || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">No campaign assignments recorded.</Typography>
            ) : (
              (data?.assignments || []).map((item: any) => (
                <Box key={item.id} sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {item.startTime} - {item.endTime} | {item.campaignName}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <Chip size="small" label={item.assignmentTypeLabel} />
                    {item.absentEmployeeName && (
                      <Typography variant="caption">Absent: {item.absentEmployeeName}</Typography>
                    )}
                    {item.createdByName && (
                      <Typography variant="caption">Created by {item.createdByName}</Typography>
                    )}
                  </Stack>
                </Box>
              ))
            )}

            {(data?.coverageRelationships || []).length > 0 && (
              <>
                <Typography variant="subtitle2">Coverage Relationships</Typography>
                {(data?.coverageRelationships || []).map((rel: any) => (
                  <Box key={`${rel.type}-${rel.coverageAssignmentId}`} sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="body2">
                      {rel.type === 'covering'
                        ? `Covering ${rel.absentEmployeeName} on ${rel.targetCampaignName}`
                        : `Covered by ${rel.coveringEmployeeName || 'Unknown'}`}
                    </Typography>
                    <Typography variant="caption" display="block">
                      {formatCoverageModeLabel(rel.coverageMode)} | {rel.coverageStartTime || '-'} - {rel.coverageEndTime || '-'}
                    </Typography>
                  </Box>
                ))}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AgentTimelineModal;
